import { createContext, useContext, useMemo } from 'react';
import { isEmpty, isNumber, isNil } from 'lodash-es';
import PropTypes from 'prop-types';
import { useObservable, useObservableState } from 'observable-hooks';
import { distinctUntilChanged, map, of, switchMap } from 'rxjs';
import { FormContext } from './utils';
import { LayoutHelper } from './layout-helper';
import InputField from './fields/InputField';
import ChoiceField from './fields/ChoiceField';
import BooleanField from './fields/BooleanField';
import InformationTooltip from './InformationTooltip';
import { inlineFieldGroupClasses } from './fields/genericRowField/GenericRowField';

const formFieldComponents = {
    char: InputField,
    integer: InputField,
    float: InputField,
    choice: ChoiceField,
    boolean: BooleanField,
};

function useGroupHiddenByConfigHelper(fieldNames) {
    const { configHelper } = useContext(FormContext);

    return useObservableState(
        useObservable(
            (inputs$) =>
                inputs$.pipe(
                    switchMap(([iFieldNames, iConfigHelper]) => {
                        if (!iConfigHelper.isEnabledForField(iFieldNames)) {
                            // Return original options.
                            return of(false);
                        }

                        return iConfigHelper.availableFields$.pipe(
                            // Check if at least one field name is available;
                            // if not, mark group as hidden.
                            map((availableFields) => {
                                const shouldBeVisible = fieldNames.some(
                                    (fieldName) => fieldName in availableFields
                                );
                                return !shouldBeVisible;
                            }),
                            // Avoid unnecessary emissions/rerenders.
                            distinctUntilChanged()
                        );
                    })
                ),
            [fieldNames, configHelper]
        ),
        false
    );
}
function RootGroupContainer({ children, isRootLevel, name }) {
    if (!isRootLevel) {
        return <div>{children}</div>;
    }

    return (
        <div className="odd:bg-main-background-odd">
            <div className="main-container-new mx-auto py-10" id={name}>
                {children}
            </div>
        </div>
    );
}
RootGroupContainer.propTypes = {
    name: PropTypes.string.isRequired,
    isRootLevel: PropTypes.bool.isRequired,
    children: PropTypes.node.isRequired,
};

const FormFieldOverridesContext = createContext({});

// Form field component that will find the best suiting form field component
// for the given form field type in the settings.
// Also allows for custom form field component overrides by form field name.
export function FormFieldLookup({ name, settings, formFieldOverrides }) {
    const FormField = formFieldOverrides?.[name] ?? formFieldComponents[settings.type];

    if (!FormField) {
        throw new Error(`Could not find field component for ${name} of type ${settings.type}`);
    }

    return (
        <FormField
            fieldOptions={{
                name,
                settings,
            }}
        />
    );
}
FormFieldLookup.propTypes = {
    name: PropTypes.string.isRequired,
    settings: PropTypes.object.isRequired,
    formFieldOverrides: PropTypes.objectOf(PropTypes.func.isRequired),
};

function Group({ group: { name, title, fields, groups }, level = 2 }) {
    const { information } = useContext(FormContext);
    const formFieldOverrides = useContext(FormFieldOverridesContext);

    const hidden = useGroupHiddenByConfigHelper(useMemo(() => Object.keys(fields), [fields]));

    const info = useMemo(() => information?.[`_group_${name}`], [name, information]);

    // We don't show empty groups.
    if (isEmpty(groups) && isEmpty(fields)) {
        return null;
    }

    const Level = `h${level}`;

    if (hidden) {
        return null;
    }

    const isRootLevel = level === 2;

    return (
        <RootGroupContainer isRootLevel={isRootLevel} name={name}>
            {title && (
                <Level className={isRootLevel ? 'mb-3.5 font-bold' : ''}>
                    {title}
                    {info && (
                        <InformationTooltip
                            information={info}
                            className="ml-2 align-middle text-form-text"
                        />
                    )}
                </Level>
            )}
            {groups ? (
                // If this group contains more groups, create sub groups.
                <div className="flex flex-col gap-y-2.5">
                    {groups.map((group) => (
                        <Group key={group.name} group={group} level={level + 1} />
                    ))}
                </div>
            ) : (
                // If this group did not contain sub groups, create the form fields.
                <ul className={inlineFieldGroupClasses}>
                    {Object.entries(fields).map(([key, value]) => (
                        <FormFieldLookup
                            key={key}
                            name={key}
                            settings={value}
                            formFieldOverrides={formFieldOverrides}
                        />
                    ))}
                </ul>
            )}
        </RootGroupContainer>
    );
}

const groupShape = {
    name: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    fields: PropTypes.object,
};
groupShape.groups = PropTypes.arrayOf(PropTypes.shape(groupShape));

Group.propTypes = {
    group: PropTypes.shape(groupShape),
    level(props, propName, componentName) {
        const given = props[propName];
        if (!isNil(given) && (!isNumber(given) || given < 2)) {
            return new Error(
                `Invalid prop \`${propName}\` supplied to` +
                    ` \`${componentName}\`. \`${propName}\`` +
                    ` should be 2 or more, but got \`${given}\`.`
            );
        }
        return null;
    },
};

function Layout({ layout, fields, formFieldOverrides }) {
    const treeWithFields = useMemo(() => {
        const layoutHelper = new LayoutHelper(layout, fields);
        return layoutHelper.createTree();
    }, [layout, fields]);

    return (
        <FormFieldOverridesContext.Provider value={formFieldOverrides}>
            {treeWithFields.map((group) => (
                <Group key={group.name} group={group} />
            ))}
        </FormFieldOverridesContext.Provider>
    );
}
Layout.propTypes = {
    layout: PropTypes.array.isRequired,
    fields: PropTypes.array.isRequired,
    formFieldOverrides: PropTypes.objectOf(PropTypes.func.isRequired),
};

export default Layout;
